import React, { useEffect, useState } from "react";
import {
  IonHeader,
  IonToolbar,
  IonButtons,
  IonMenuButton,
  IonTitle,
  IonContent,
  IonPage,
} from "@ionic/react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tabs,
  Tab,
  Box,
  TextField,
  Chip,
  Pagination,
  Alert,
} from "@mui/material";
import axios from "axios";
import { baseUrl, config, requestID } from "../../../common/utils";

type PurchaseOrder = {
  id: string;
  product_name: string;
  request_id: string;
  product_id: string;
  quantity: number;
  approved_qty: number;
  audit_qty: number;
  unit_price: string;
  total_price: string;
  status: string;
  suppliedStatus?: string;
};

const statusFilters = [
  { value: "unaudited", label: "Unaudited" },
  { value: "audited", label: "Audited" },
];

const PurchaseOrderAudit = () => {
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<PurchaseOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState<"audited" | "unaudited">("unaudited");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(8);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState<string>("");

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    id: string;
    action: "approved" | "rejected";
  }>({ open: false, id: "", action: "approved" });

  const isAuditMode = filter === "unaudited";

  useEffect(() => {
    fetchOrders();
  }, [filter]);

  useEffect(() => {
    filterOrders();
  }, [orders, searchTerm]);

  const fetchOrders = () => {
    setIsLoading(true);
    setError("");
    const formData = new FormData();
    formData.append("requestID", requestID());

    const endpoint =
      filter === "audited"
        ? "warehouses/getApprovedAuditOrder"
        : "warehouses/getUnapprovedAuditOrder";

    axios
      .post(baseUrl() + endpoint, formData, config)
      .then((res) => {
        console.log("API Response:", res);
        
        let ordersArray = [];
        
        // Handle different API response structures
        if (Array.isArray(res.data)) {
          ordersArray = res.data;
        } else if (res.data?.data) {
          ordersArray = res.data.data;
        } else if (res.data?.orders) {
          ordersArray = res.data.orders;
        } else if (res.data) {
          // If res.data is an object but not in expected format
          ordersArray = [res.data];
        }
        
        console.log("Parsed orders array:", ordersArray);

        const processedOrders = ordersArray.map((order: any) => ({
          ...order,
          id: order.id || order.request_id + "_" + order.product_id,
          quantity: Number(order.quantity) || 0,
          approved_qty: Number(order.approved_qty) || 0,
          audit_qty: Number(order.audit_qty) || Number(order.quantity) || 0,
          unit_price: order.unit_price || "0",
          total_price:
            order.total_price ||
            (
              Number(order.audit_qty || order.quantity || 0) *
              Number(order.unit_price || 0)
            ).toFixed(2),
          status: order.status || "unaudited",
          suppliedStatus: order.suppliedStatus,
        }));
        
        console.log("Processed orders:", processedOrders);
        setOrders(processedOrders);
      })
      .catch((err) => {
        console.error(`Error fetching ${filter} orders:`, err);
        setError(`Failed to fetch ${filter} purchase orders: ${err.message}`);
      })
      .finally(() => setIsLoading(false));
  };

  const filterOrders = () => {
    let filtered = [...orders];

    if (searchTerm) {
      filtered = filtered.filter(
        (order) =>
          order.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.request_id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredOrders(filtered);
    setCurrentPage(1);
  };

  const handleQuantityChange = (id: string, value: string) => {
    const numValue = Number(value);
    const updatedOrders = orders.map((order) => {
      if (order.id === id) {
        // Enforce max value
        const safeValue = Math.min(Math.max(numValue, 0), order.approved_qty);
        
        return {
          ...order,
          audit_qty: safeValue,
          total_price: (safeValue * Number(order.unit_price || 0)).toFixed(2),
        };
      }
      return order;
    });
    setOrders(updatedOrders);
  };

  const confirmStatusChange = (id: string, action: "approved" | "rejected") => {
    const order = orders.find((o) => o.id === id);
    if (!order) {
      setError("Order not found");
      return;
    }
    
    if (order.audit_qty <= 0) {
      setError("Audit quantity must be greater than 0");
      return;
    }
    
    setConfirmDialog({ open: true, id, action });
  };

  const handleStatusChange = () => {
    const { id, action } = confirmDialog;
    const order = orders.find((o) => o.id === id);
    
    if (!order) {
      setError("Order not found");
      setConfirmDialog({ open: false, id: "", action: "approved" });
      return;
    }

    const formData = new FormData();
    formData.append("requestID", requestID());
    formData.append("request_id", order.request_id);
    formData.append("product_id", order.product_id);
    formData.append("status", action);
    formData.append("quantity", String(order.audit_qty));

    console.log("Sending audit approval:", {
      request_id: order.request_id,
      product_id: order.product_id,
      quantity: order.audit_qty,
      status: action
    });

    axios
      .post(baseUrl() + "warehouses/approveAuditOrder", formData, config)
      .then((res) => {
        console.log("Audit approval response:", res);
        alert(`Order ${action} successfully`);
        fetchOrders(); // Refresh the list
      })
      .catch((err) => {
        console.error("Error approving audit order:", err);
        setError("Failed to update order status: " + err.message);
      })
      .finally(() => {
        setConfirmDialog({ open: false, id: "", action: "approved" });
      });
  };

  const handleFilterChange = (
    event: React.SyntheticEvent,
    newValue: "audited" | "unaudited"
  ) => {
    setFilter(newValue);
    setSearchTerm(""); // Clear search when changing filter
  };

  // Pagination logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredOrders.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredOrders.length / rowsPerPage) || 1;

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const getStatusChip = (status?: string) => {
    switch (status) {
      case "1":
        return <Chip label="Supplied" color="success" variant="outlined" size="small" />;
      case "0":
        return <Chip label="Not Supplied" color="error" variant="outlined" size="small" />;
      default:
        return (
          <Chip label="Pending Audit" color="warning" variant="outlined" size="small" />
        );
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Purchase Order Audit</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <Box sx={{ p: 3 }}>
          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {/* Filter and Search Section */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            <TextField
              label="Search Products or Request ID"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: { xs: "100%", sm: 300 } }}
            />

            <Tabs
              value={filter}
              onChange={handleFilterChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {statusFilters.map((tab) => (
                <Tab key={tab.value} label={tab.label} value={tab.value} />
              ))}
            </Tabs>
          </Box>

          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50vh",
                flexDirection: "column",
              }}
            >
              <CircularProgress />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Loading {filter} purchase orders...
              </Typography>
            </Box>
          ) : currentRows.length > 0 ? (
            <>
              <TableContainer
                component={Paper}
                elevation={3}
                sx={{
                  mb: 3,
                  maxHeight: "calc(100vh - 300px)",
                  overflow: "auto",
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="right">
                        Request ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="right">
                        Approved Qty
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="right">
                        Received Qty
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                      {isAuditMode && (
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Actions
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentRows.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>{order.product_name}</TableCell>
                        <TableCell align="right">{order.request_id}</TableCell>
                        <TableCell align="right">
                          {order.approved_qty}
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={order.audit_qty}
                            onChange={(e) => {
                              if (!isAuditMode) return;
                              handleQuantityChange(order.id, e.target.value);
                            }}
                            inputProps={{
                              min: 0,
                              max: order.approved_qty,
                            }}
                            sx={{ width: 80 }}
                            disabled={!isAuditMode}
                          />
                        </TableCell>
                        <TableCell>
                          {getStatusChip(order.suppliedStatus)}
                        </TableCell>
                        {isAuditMode && (
                          <TableCell>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() =>
                                confirmStatusChange(order.id, "approved")
                              }
                              disabled={order.audit_qty <= 0}
                            >
                              Approve
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}

              {/* Summary */}
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                }}
              >
                <Typography variant="subtitle1">
                  Total Items: <strong>{filteredOrders.length}</strong>
                </Typography>
                {isAuditMode && (
                  <Typography variant="subtitle1">
                    Items Ready for Approval:{" "}
                    <strong>
                      {filteredOrders.filter(order => order.audit_qty > 0).length}
                    </strong>
                  </Typography>
                )}
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50vh",
                flexDirection: "column",
              }}
            >
              <Typography variant="h6" sx={{ mb: 1 }}>
                No {filter} purchase orders found
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {searchTerm
                  ? "Try a different search term"
                  : "Check back later for new orders"}
              </Typography>
            </Box>
          )}

          {/* Confirmation Dialog */}
          <Dialog
            open={confirmDialog.open}
            onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>Confirm Audit Approval</DialogTitle>
            <DialogContent>
              <Typography gutterBottom>
                Are you sure you want to approve this order with the received quantity?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() =>
                  setConfirmDialog({ ...confirmDialog, open: false })
                }
                color="inherit"
              >
                Cancel
              </Button>
              <Button
                onClick={handleStatusChange}
                color="primary"
                variant="contained"
              >
                Confirm Approval
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default PurchaseOrderAudit;